import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, Ref } from 'vue';
import { FilterConfiguration, NullableString } from './types';
import { PropType } from 'vue';
import { DEFAULT_COL_PROPS } from './constants';

/**
 * DynamicFilterBuilder component
 *
 * This component dynamically builds filter components based on the provided configuration.
 *
 * @component
 *
 * @example
 * &lt;dynamic-filter-builder :config="filterConfig" @on-change="onFilterChanged" /&gt;
 * const filterConfig: FilterConfiguration[] = [
 * {
 *   type: FilterType.INPUT,
 *   props: { label: "Vorname" },
 * }
 *
 *
 * You can use two way binding by passing a model to the filter configuration
 * @example
 * &lt;dynamic-filter-builder :config="filterConfig" /&gt;
 *
 * const filterConfig: FilterConfiguration[] = [
 * {
 *   type: FilterType.INPUT,
 *   model: ref("model value"),
 * }]
 *
 * You can make the layout responsive by passing colProps to the filter configuration
 * @example
 * &lt;dynamic-filter-builder :config="filterConfig" @on-change="onFilterChange" /&gt;
 *
 * const filterConfig: FilterConfiguration[] = [
 * {
 *   type: FilterType.INPUT,
 *   props: { label: "Vorname" },
 *   colProps: { cols: 12 }, // full width of the row
 * }]
 *
 */

/**
 * Emits an event when any filter component triggers onChange or onInput.
 *
 * @event on-change
 * @type {object}
 * @property {NullableString[]} values - The new filter values. Order of values corresponds to the order of the filter components.
 *
 * @event on-input
 * @type {object}
 * @property {NullableString[]} values - The new filter values. Order of values corresponds to the order of the filter components.
 */
interface ComponentDefinition {
  type: string;
  props?: object;
  model?: Ref<any>;
  onChange?: (value: NullableString) => void;
  onInput?: (value: NullableString) => void;
  items?: any;
  colProps?: object;
}

/**
 * Array of child components to be rendered in the template
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'DynamicFilterBuilder',
  props: {
  /**
   * The configuration for the filters.
   * @type {FilterConfiguration[]}
   */
  config: {
    type: Array as PropType<FilterConfiguration[]>,
    required: true
  }
},
  emits: ["on-input", "on-change"],
  setup(__props, { emit }: { emit: ({
  (event: 'on-input' | 'on-change', values: NullableString[]): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;



/**
 * Property definitions
 */


const childComponentsArray =  ref<ComponentDefinition[]>([]);

/**
 * Fire filter event when any filter component triggers onChange or onInput
 * @param event - event to be fired
 */
const fireFilterEvent = (event : 'on-change' | 'on-input') =>{
  const currentValues = childComponentsArray.value.map(r => r.model);
  emit(event, currentValues);
}

const setupChildComponents = () => {
  // process the passed configuration and build the components to be used in the template
  childComponentsArray.value = props.config.map((filterConfig: FilterConfiguration) => {
    return {
      type: filterConfig.type,
      props: filterConfig.props,
      model: filterConfig.model || ref(null),
      onChange: (value: NullableString) => {
        filterConfig.events?.onChange?.(value);
        fireFilterEvent('on-change');
      },
      onInput: (value: NullableString) => {
        filterConfig.events?.onInput?.(value);
        fireFilterEvent('on-input');
      },
      items: filterConfig.items,
      colProps: {
        ...DEFAULT_COL_PROPS,
        ...filterConfig.colProps
      }
    };
  });
}

onMounted(() => {
  if(props.config && props.config.length > 0) {
    setupChildComponents();
  }
});

return { __sfc: true,emit, props, childComponentsArray, fireFilterEvent, setupChildComponents }
}

})